import React from "react";

import { WidgetEmptyState } from "../../WidgetRenderer/comps/altStatePlaceholders";
import { WIDGET_ELEM_ConfigForm } from "./ConfigForm";
import { TEMP_WIDGET_PROPS_KEY } from "./DATA_KEYS";
import { CUSTOM_WIDGET_LABEL } from "./labels";
import { asTempWidgetConfig, getTempWidget, getTempWidgetOption } from "./TEMP_WIDGETS";

/**
 * WidgetTemp_SHI — a container for experimental ("temp") widgets.
 *
 * One entry in the Add Widget list, many widgets behind it: picking it opens a
 * second selector inside the config panel listing every temp widget in
 * ./tempWidgets/_EXPORTS.js, and from there the container is a pass-through.
 *
 *   Add Widget ─▸ Custom Widget ─▸ [ Select widget: ▾ ] ─▸ that widget's own config
 *
 * WHY a container rather than one Add Widget entry per experiment: experiments
 * come and go, and each one that ships as a top-level widget puts a permanent
 * row in every tenant's Add Widget list and a permanent folder in the widget
 * registry. Here the blast radius of adding or deleting one is a single line in
 * ./tempWidgets/_EXPORTS.js.
 *
 * The contract a temp widget implements is deliberately the SAME one a
 * top-level widget implements (Component / optionDef / ConfigForm /
 * processRawConfigFn / someReqPropsUnset) — see ./README.md. So promoting a
 * temp widget to a real one later is a move, not a rewrite.
 */

/**
 * Who may ADD this from the Add Widget list: SELF-MANAGED tenants, and any
 * legacy tenant whose deployment config opts in.
 *
 * `SELF_MANAGED_BASE_CONFIGS.enable` is the real flag, and the only correct one
 * — the set of self-managed tenants is a RUNTIME fact (tenantRegistry.json in
 * GCS), so a tenant-id list in this file could only ever be a hand-kept copy of
 * it that goes stale the next time a tenant is provisioned.
 *
 * `deployment._EnableCustomReportWidgets.enable` is the opt-in for everyone
 * else — a Platform Builder switch (okf-be deployment defaults/tpl), flipped
 * per tenant. MAD is the first: its Child Progress widget lives here.
 *
 * WHY this is a predicate and not a conditional `optionDef`: the flag rides the
 * FETCHED platformConfigs query, and `optionDef` is read exactly once, at module
 * import, by `allWidgetsBaseConfs`. Only the tenant id is available that early
 * (`window.__TENANT__`) — which is the sole reason the old `getTenant()` gate
 * could work at all. So the gate is evaluated at RENDER time instead, by
 * useAddableWidgetsBaseConfs in ReportLanding/CONFIG.js.
 *
 * Keeping `optionDef` unconditional also fixes a quiet bug in the old gate:
 * emptying it removed the entry from `allWidgetsBaseConfs`, and WidgetRenderer
 * resolves a saved widget's widgetLabel, title fallback and under-construction
 * display out of that same list — so a custom widget already on a report
 * rendered unlabelled and untitled on every non-SHI tenant.
 *
 * Only ADDING is gated. A saved widget still renders anywhere, deliberately: a
 * gated-off report should not break.
 *
 * NOTE there is no env gate: self-managed PROD tenants get these experiments
 * too. If that needs walking back, the codebase's lever is
 * okf-be/FEATURES_DISABLED_ON_PROD.js (how smDashboardLenses et al. are gated),
 * which would slot into this predicate without touching anything else.
 */
const isAvailableToAdd = ({ platformConfigs }) =>
  platformConfigs?.SELF_MANAGED_BASE_CONFIGS?.enable === true ||
  platformConfigs?.deployment?._EnableCustomReportWidgets?.enable === true;

export const Component = ({ tempWidget, ...runtimeProps }) => {
  const entry = getTempWidget(tempWidget);

  // Normally unreachable: someReqPropsUnset below already sends "nothing
  // picked" and "picked something that has since been deleted" to the
  // under-construction placeholder. This is the guard for a config that
  // reaches the renderer another way (a snapshot replay, an AI-authored
  // config) naming a temp widget this build doesn't have.
  if (!entry?.Component) {
    return <WidgetEmptyState display="Select a temp widget" />;
  }

  const TempWidgetComp = entry.Component;
  const { [TEMP_WIDGET_PROPS_KEY]: tempWidgetProps = {}, ...sharedProps } = runtimeProps;

  // The temp widget's OWN props first, then everything the renderer supplies
  // (id, widgetIdx, activeFilters, style, prefetchedData, isPublicReport,
  // columnSpan...). Shared props win, so a temp widget cannot accidentally
  // shadow the runtime contract by storing a key of the same name.
  return <TempWidgetComp {...tempWidgetProps} {...sharedProps} />;
};

export const optionDef = [
  {
    display: CUSTOM_WIDGET_LABEL,
    icon: "Flask",
    value: "tempShi",
    comp: "WidgetTemp_SHI",
    isAvailableToAdd,
  },
];

export const defaultConfig = { props: {} };

/**
 * Delegate to the picked temp widget, handing it a config whose `props` ARE its
 * own slice, and folding whatever it returns back into that slice. A temp
 * widget without one is left untouched, exactly like a top-level widget without
 * one (see WidgetRenderer).
 */
export const processRawConfigFn = (_widgetConfig) => {
  const entry = getTempWidget(_widgetConfig?.props?.tempWidget);

  /**
   * The shell reads `props.title` for the widget header, falling back to the
   * option's `display` — which here is the container's name, so every custom
   * widget on a report would read "Custom Widget". Default it to the PICKED
   * widget's name instead.
   *
   * Injected into the PROCESSED config only, never written back: leaving
   * props.title unset is what keeps the title input showing a derived default
   * with its "reset to default" affordance (see useWidgetTitle), rather than
   * silently becoming a value the author now has to maintain.
   */
  const withTitle = (props) => ({
    ...props,
    title: props?.title || getTempWidgetOption(props?.tempWidget)?.display,
  });

  if (!entry?.processRawConfigFn) {
    return { ..._widgetConfig, props: withTitle(_widgetConfig.props) };
  }

  const processed = entry.processRawConfigFn(asTempWidgetConfig(_widgetConfig));

  // A temp widget can qualify the title with what its current settings show
  // ("… — percent change") by returning `titleSuffix` in its processed props.
  // Appended to the rendered title only, custom or default — never written
  // back, so the title input keeps the author's own words.
  const titled = withTitle(_widgetConfig.props);
  const titleSuffix = processed?.props?.titleSuffix;

  // The report's filter sidebar builds its filters from the templates the
  // report's widgets read (ReportLanding.getSelectedTpls, off the processed
  // config's props). A temp widget names its own — possibly several, when it
  // switches between templates — by returning `contentTypes`; lifted here to
  // where the sidebar looks, the way a top-level widget's `contentType` sits.
  const contentTypes = processed?.props?.contentTypes;

  return {
    ..._widgetConfig,
    props: {
      ...titled,
      ...(titleSuffix ? { title: `${titled.title} — ${titleSuffix}` } : {}),
      ...(contentTypes?.length ? { contentTypes } : {}),
      [TEMP_WIDGET_PROPS_KEY]: processed?.props || {},
    },
  };
};

/**
 * Unset until a temp widget is picked — that IS this widget's one required
 * prop. Past that, the picked widget answers for itself.
 */
export const someReqPropsUnset = (widgetConfig) => {
  const entry = getTempWidget(widgetConfig?.props?.tempWidget);
  if (!entry) return true;
  if (!entry.someReqPropsUnset) return false;
  return entry.someReqPropsUnset(asTempWidgetConfig(widgetConfig));
};

export const ConfigForm = WIDGET_ELEM_ConfigForm;
